import React, { useEffect, useMemo, useState } from "react";
import { useSelector, shallowEqual } from "react-redux";
import * as apiLearning from "../../../api/learning";
import * as apiTarget from "../../../api/target";
import Select from "react-select";
import { isLearningAdmin } from "./learningPermissions";
import * as yup from "yup";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTrash,
  faUpload,
  faSave,
  faDownload,
  faExclamationTriangle,
  faSpinner,
  faFileVideo,
  faLink,
  faClock,
  faToggleOn,
  faToggleOff,
  faPlus,
  faCheckCircle,
  faCircle
} from "@fortawesome/free-solid-svg-icons";
import zIndex from "@material-ui/core/styles/zIndex";

// ✅ Toast semplice (riusabile — puoi spostarlo in utils/toast.js)
const showToast = (message, type = "info") => {
  const id = `toast-${Date.now()}`;
  const color = type === "error" ? "tw:bg-red-500" : type === "success" ? "tw:bg-green-500" : "tw:bg-blue-500";
  const el = document.createElement("div");
  el.id = id;
  el.innerHTML = `
    <div class="tw:fixed tw:top-4 tw:right-4 tw:max-w-sm tw:z-50 tw:px-4 tw:py-3 tw:rounded-sm tw:text-white tw:shadow-lg ${color} tw:flex tw:items-center tw:gap-2">
      <span>${message}</span>
      <button class="tw:ml-auto tw:text-white/80 hover:tw:text-white" onclick="document.getElementById('${id}').remove()">✕</button>
    </div>
  `;
  document.body.appendChild(el);
  setTimeout(() => {
    const e = document.getElementById(id);
    if (e) e.remove();
  }, 4000);
};

export default function LearningVideos() {
  const { user } = useSelector(({ auth }) => auth, shallowEqual);
  const token = user?.authToken;
  const [videos, setVideos] = useState([]);
  const [targets, setTargets] = useState([]);
  const [selectedVideoId, setSelectedVideoId] = useState("");
  const [form, setForm] = useState({ videoId: "", title: "", url: "", durationSec: 0, active: 1 });
  const [videoTargets, setVideoTargets] = useState([]);
  const [diskVideos, setDiskVideos] = useState([]);
  const [selectedDisk, setSelectedDisk] = useState(null);
  const [fileToUpload, setFileToUpload] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState({ save: false, saveTargets: false, delete: false, deleteFile: false, disk: false });
  const [confirmModal, setConfirmModal] = useState({ open: false, onConfirm: null, message: "", action: "" });

  const targetOptions = useMemo(() => targets.map((t) => ({ value: t.id, label: t.name })), [targets]);
  const selectedVideo = useMemo(() => videos.find((v) => v.video_id === selectedVideoId) || null, [
    videos,
    selectedVideoId,
  ]);

  const myRole = parseInt(user.idRole);
  const canUpload = isLearningAdmin(myRole);

  // ✅ Classi riutilizzabili (mantenute, ma migliorate)
  const twInput = `
    tw:w-full
    tw:px-4 tw:py-2.5
    tw:rounded-sm
    tw:border tw:border-gray-200
    tw:bg-white
    tw:text-gray-800
    tw:placeholder-gray-400
    tw:shadow-sm
    focus:tw:outline-none
    focus:tw:ring-2 focus:tw:ring-blue-500/20
    focus:tw:border-blue-500
    tw:transition tw:duration-200
  `.trim();

  const twBtnBase = `
    tw:inline-flex tw:items-center tw:gap-2
    tw:px-4 tw:py-2.5
    tw:font-medium tw:rounded-sm
    tw:shadow-sm
    tw:transition tw:duration-200
    hover:tw:scale-[1.02] tw:active:scale-[0.98]
  `.trim();

  // ✅ Stile react-select coerente
  const selectStyles = {
    control: (base, state) => ({
      ...base,
      borderColor: state.isFocused ? "#3b82f6" : "#d1d5db",
      boxShadow: state.isFocused ? "0 0 0 3px rgba(59, 130, 246, 0.2)" : "none",
      "&:hover": { borderColor: "#3b82f6" },
      borderRadius: "0.5rem",
      padding: "2px",
    }),
    menu: (base) => ({ ...base, zIndex: 99999 }),
    menuPortal: (base) => ({ ...base, zIndex: 99999 }),
  };

  useEffect(() => {
    if (!token) return;
    apiLearning.getVideos(token).then((data) => setVideos(Array.isArray(data) ? data : []));
    apiTarget.getTargets(token).then((data) => setTargets(Array.isArray(data) ? data : []));
    refreshDisk();
  }, [token]);

  useEffect(() => {
    if (!token || !selectedVideoId) return;
    apiLearning.getVideoTargets(token, selectedVideoId).then((r) => {
      const ids = Array.isArray(r?.targetIds) ? r.targetIds : [];
      setVideoTargets(ids);
    });
  }, [token, selectedVideoId]);

  useEffect(() => {
    if (!selectedVideo) return;
    setForm({
      videoId: selectedVideo.video_id,
      title: selectedVideo.title || "",
      url: selectedVideo.url || "",
      durationSec: selectedVideo.duration_sec || 0,
      active: selectedVideo.active || 1,
    });
  }, [selectedVideo]);

  const computeDurationFromUrl = async (url) => {
    return new Promise((resolve) => {
      const video = document.createElement("video");
      video.preload = "metadata";
      video.src = url;
      video.onloadedmetadata = () => resolve(Math.round(video.duration || 0));
      video.onerror = () => resolve(0);
    });
  };

  useEffect(() => {
    const run = async () => {
      if (!selectedDisk?.url) return;
      const dur = await computeDurationFromUrl(selectedDisk.url);
      setForm((f) => ({
        ...f,
        url: selectedDisk.url,
        title: f.title || selectedDisk.filename.replace(/\.[^/.]+$/, ""),
        durationSec: dur,
      }));
    };
    run();
  }, [selectedDisk]);

  const schema = useMemo(
    () =>
      yup.object({
        videoId: yup
          .string()
          .trim()
          .required("Video ID obbligatorio"),
        title: yup
          .string()
          .trim()
          .required("Titolo obbligatorio"),
        url: yup
          .string()
          .trim()
          .required("URL obbligatorio"),
        durationSec: yup
          .number()
          .typeError("Durata deve essere un numero")
          .integer("Durata deve essere intera")
          .min(1, "Durata minima 1s")
          .required("Durata obbligatoria"),
        active: yup
          .number()
          .oneOf([0, 1])
          .required(),
      }),
    []
  );

  const validateForm = (data) => {
    try {
      schema.validateSync(data, { abortEarly: false });
      setErrors({});
      return true;
    } catch (err) {
      const next = {};
      if (err?.inner)
        err.inner.forEach((e) => {
          if (e.path && !next[e.path]) next[e.path] = e.message;
        });
      setErrors(next);
      return false;
    }
  };

  const handleSaveVideo = async () => {
    if (!token || !validateForm(form)) return;
    setLoading((prev) => ({ ...prev, save: true }));
    try {
      await apiLearning.setVideo(token, form);
      const updated = await apiLearning.getVideos(token);
      setVideos(Array.isArray(updated) ? updated : []);
      setSelectedVideoId(form.videoId);
      showToast("✅ Video salvato", "success");
    } catch (e) {
      showToast(`❌ ${e?.plainMessage || "Errore salvataggio"}`, "error");
    } finally {
      setLoading((prev) => ({ ...prev, save: false }));
    }
  };

  const handleSaveTargets = async () => {
    if (!token || !form.videoId) return;
    setLoading((prev) => ({ ...prev, saveTargets: true }));
    try {
      await apiLearning.setVideoTargets(token, form.videoId, videoTargets);
      showToast("✅ Target salvati", "success");
    } catch (e) {
      showToast(`❌ ${e?.plainMessage || "Errore salvataggio target"}`, "error");
    } finally {
      setLoading((prev) => ({ ...prev, saveTargets: false }));
    }
  };

  const refreshDisk = async () => {
    setLoading((prev) => ({ ...prev, disk: true }));
    try {
      const data = await apiLearning.listDiskVideos(token);
      setDiskVideos(Array.isArray(data) ? data : []);
    } finally {
      setLoading((prev) => ({ ...prev, disk: false }));
    }
  };

  const handleUpload = async () => {
    if (!token || !fileToUpload) return;
    setUploading(true);
    try {
      await apiLearning.uploadVideo(token, fileToUpload);
      await refreshDisk();
      setFileToUpload(null);
      showToast("✅ File caricato", "success");
    } catch (e) {
      showToast(`❌ ${e?.plainMessage || "Errore upload"}`, "error");
    } finally {
      setUploading(false);
    }
  };

  const handleCreateFromDisk = async () => {
    if (!token || !selectedDisk || !validateForm(form)) return;
    setLoading((prev) => ({ ...prev, save: true }));
    try {
      await apiLearning.setVideo(token, form);
      await refreshDisk();
      const updated = await apiLearning.getVideos(token);
      setVideos(Array.isArray(updated) ? updated : []);
      setSelectedDisk(null);
      showToast("✅ Video creato", "success");
    } catch (e) {
      showToast(`❌ ${e?.plainMessage || "Errore creazione"}`, "error");
    } finally {
      setLoading((prev) => ({ ...prev, save: false }));
    }
  };

  const showDeleteConfirm = (video) => {
    setConfirmModal({
      open: true,
      message: `Eliminare il video "${video.title}"?`,
      action: "delete",
      onConfirm: async () => {
        setConfirmModal({ open: false, onConfirm: null, message: "", action: "" });
        await performDelete(video);
      },
    });
  };

  const performDelete = async (video) => {
    setLoading((prev) => ({ ...prev, delete: true }));
    try {
      await apiLearning.deleteVideo(token, video.video_id);
      // Opzionale: chiedi se eliminare anche il file (qui facciamo solo DB; puoi estendere come in Swal)
      const updated = await apiLearning.getVideos(token);
      setVideos(Array.isArray(updated) ? updated : []);
      if (selectedVideoId === video.video_id) setSelectedVideoId("");
      showToast("🗑️ Video eliminato", "success");
    } catch (e) {
      showToast(`❌ ${e?.plainMessage || "Errore eliminazione"}`, "error");
    } finally {
      setLoading((prev) => ({ ...prev, delete: false }));
    }
  };

  const showDeleteFileConfirm = (diskVideo) => {
    setConfirmModal({
      open: true,
      message: `Eliminare il file "${diskVideo.filename}"?`,
      action: "delete-file",
      onConfirm: async () => {
        setConfirmModal({ open: false, onConfirm: null, message: "", action: "" });
        await performDeleteFile(diskVideo);
      },
    });
  };

  const performDeleteFile = async (diskVideo) => {
    setLoading((prev) => ({ ...prev, deleteFile: true }));
    try {
      await apiLearning.deleteVideoFile(token, diskVideo.url);
      await refreshDisk();
      if (selectedDisk?.url === diskVideo.url) setSelectedDisk(null);
      showToast("🗑️ File eliminato", "success");
    } catch (e) {
      showToast(`❌ ${e?.plainMessage || "Errore eliminazione file"}`, "error");
    } finally {
      setLoading((prev) => ({ ...prev, deleteFile: false }));
    }
  };

  return (
    <div className="tw:max-w-7xl tw:mx-auto tw:px-4 tw:py-6 tw:space-y-6">
      {!canUpload ? (
        <div className="tw:bg-amber-50 tw:border tw:border-amber-200 tw:text-amber-800 tw:rounded-xl tw:p-4 tw:text-center">
          🔒 Questa area è riservata agli amministratori, agli sviluppatori e ai grafici.
        </div>
      ) : (
        <>
          {/* === Upload Section === */}
          <section className="tw:bg-white tw:rounded-xl tw:shadow-none tw:overflow-hidden tw:border tw:border-gray-100">
            <div
              onClick={() => setShowUpload((s) => !s)}
              className="tw:flex tw:items-center tw:justify-between tw:px-6 tw:py-4 tw:bg-gradient-to-r tw:from-green-50 tw:to-emerald-50"
            >
              <h2 className="tw:text-xl tw:text-gray-800 tw:flex tw:items-center tw:gap-2">
                <FontAwesomeIcon icon={faUpload} />
                Carica e Crea Video
              </h2>
              <button
                type="button"
                className={`${
                  !showUpload ? "tw:bg-blue-600" : "tw:bg-gray-400"
                } tw:text-white tw:text-sm tw:font-medium tw:px-4 tw:py-2 tw:rounded-md tw:flex tw:items-center tw:gap-1`}
                // onClick={() => setShowUpload((s) => !s)}
              >
                {showUpload ? "Nascondi" : "Mostra"}
              </button>
            </div>
            <div
              className={`tw:overflow-hidden tw:transition-all tw:duration-300 tw:ease-in-out ${
                showUpload ? "tw:max-h-[1000px] tw:opacity-100" : "tw:max-h-0 tw:opacity-0"
              }`}
            >
              <div className="tw:p-6 tw:space-y-6">
                {/* Upload file */}
                <div className="tw:space-y-2">
                  <label className="tw:block tw:text-sm tw:font-medium tw:text-gray-700">Carica nuovo video</label>
                  <div className="tw:flex tw:items-center tw:gap-3 tw:w-full tw:justify-between">
                    <input
                      id="video-upload"
                      type="file"
                      accept="video/*"
                      className="tw:file:tw:mr-4 tw:file:tw:py-2 tw:file:tw:px-4 tw:file:tw:rounded-full tw:file:tw:border-0 tw:file:tw:text-sm tw:file:tw:font-semibold tw:file:tw:bg-blue-50 tw:file:tw:text-blue-700 hover:tw:file:tw:bg-blue-100"
                      onChange={(e) => setFileToUpload(e.target.files?.[0] || null)}
                    />
                    <button
                      type="button"
                      className="tw:inline-flex tw:items-center tw:gap-2 tw:px-4 tw:py-2.5 tw:bg-blue-600 hover:tw:bg-blue-700 tw:text-white tw:font-medium tw:rounded-sm tw:shadow tw:transition"
                      onClick={handleUpload}
                      disabled={!fileToUpload || uploading}
                    >
                      {uploading ? (
                        <FontAwesomeIcon icon={faSpinner} className="tw:animate-spin" />
                      ) : (
                        <FontAwesomeIcon icon={faUpload} />
                      )}
                      {uploading ? "Caricamento..." : "Carica"}
                    </button>
                  </div>
                </div>

                {/* Seleziona da disco */}
                <div className="tw:space-y-4">
                  <label className="tw:block tw:text-sm tw:font-medium tw:text-gray-700">
                    Video su disco (non ancora in DB)
                  </label>
                  <Select
                    id="disk-video-select"
                    options={diskVideos.map((v) => ({ value: v.url, label: v.filename }))}
                    value={selectedDisk ? { value: selectedDisk.url, label: selectedDisk.filename } : null}
                    onChange={(val) => setSelectedDisk(val ? { url: val.value, filename: val.label } : null)}
                    placeholder="— Seleziona un video —"
                    noOptionsMessage={() => "Nessun video disponibile"}
                    isLoading={loading.disk}
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                  />

                  {selectedDisk && (
                    <div className="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-4 tw:p-4 tw:bg-blue-50 tw:rounded-sm">
                      <div>
                        <dt className="tw:text-xs tw:font-medium tw:text-blue-800">File</dt>
                        <dd className="tw:mt-1 tw:text-sm tw:font-mono tw:text-blue-900">{selectedDisk.filename}</dd>
                      </div>
                      <div>
                        <dt className="tw:text-xs tw:font-medium tw:text-blue-800">URL</dt>
                        <dd className="tw:mt-1 tw:text-sm tw:truncate tw:text-blue-900">{selectedDisk.url}</dd>
                      </div>
                      <button
                        type="button"
                        className="tw:md:col-span-2 tw:inline-flex tw:items-center tw:justify-center tw:gap-2 tw:px-4 tw:py-2 tw:text-red-700 tw:font-medium tw:rounded-sm tw:shadow tw:outline-2 tw:outline-red-300"
                        onClick={() => showDeleteFileConfirm(selectedDisk)}
                        disabled={loading.deleteFile}
                      >
                        {loading.deleteFile ? <FontAwesomeIcon icon={faSpinner} className="tw:animate-spin" /> : <FontAwesomeIcon icon={faTrash} />}
                        Elimina file dal disco
                      </button>
                    </div>
                  )}

                  {/* Form creazione */}
                  <div className="tw:space-y-3">
                    <div>
                      <label
                        htmlFor="new-video-id"
                        className="tw:block tw:text-sm tw:font-medium tw:text-gray-700 tw:mb-1"
                      >
                        Scegli un ID per il video
                      </label>
                      <input
                        id="new-video-id"
                        className={`${twInput} ${errors.videoId ? "tw:border-red-400 tw:ring-1 tw:ring-red-200" : ""}`}
                        placeholder="es. v-12345"
                        value={form.videoId}
                        onChange={(e) => setForm({ ...form, videoId: e.target.value })}
                      />
                      {errors.videoId && (
                        <div className="tw:mt-1 tw:flex tw:items-start tw:gap-1 tw:text-sm tw:text-red-600">
                          <FontAwesomeIcon icon={faExclamationTriangle} className="tw:mt-0.5" />
                          {errors.videoId}
                        </div>
                      )}
                    </div>

                    <div>
                      <label
                        htmlFor="new-title"
                        className="tw:block tw:text-sm tw:font-medium tw:text-gray-700 tw:mb-1"
                      >
                        Titolo
                      </label>
                      <input
                        id="new-title"
                        className={`${twInput} ${errors.title ? "tw:border-red-400 tw:ring-1 tw:ring-red-200" : ""}`}
                        value={form.title}
                        onChange={(e) => setForm({ ...form, title: e.target.value })}
                      />
                      {errors.title && (
                        <div className="tw:mt-1 tw:flex tw:items-start tw:gap-1 tw:text-sm tw:text-red-600">
                          <FontAwesomeIcon icon={faExclamationTriangle} className="tw:mt-0.5" />
                          {errors.title}
                        </div>
                      )}
                    </div>

                    <div className="tw:hidden tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-4">
                      <div>
                        <label
                          htmlFor="new-url"
                          className="tw:block tw:text-sm tw:font-medium tw:text-gray-700 tw:mb-1"
                        >
                          URL
                        </label>
                        <div className="tw:relative">
                          <FontAwesomeIcon icon={faLink} className="tw:absolute tw:left-3 tw:top-3 tw:text-gray-400" />
                          <input
                            id="new-url"
                            className={`${twInput} tw:pl-10 ${
                              errors.url ? "tw:border-red-400 tw:ring-1 tw:ring-red-200" : ""
                            }`}
                            value={form.url}
                            onChange={(e) => setForm({ ...form, url: e.target.value })}
                          />
                        </div>
                        {errors.url && (
                          <div className="tw:mt-1 tw:flex tw:items-start tw:gap-1 tw:text-sm tw:text-red-600">
                            <FontAwesomeIcon icon={faExclamationTriangle} className="tw:mt-0.5" />
                            {errors.url}
                          </div>
                        )}
                      </div>
                      <div>
                        <label
                          htmlFor="new-duration"
                          className="tw:block tw:text-sm tw:font-medium tw:text-gray-700 tw:mb-1"
                        >
                          Durata (sec)
                        </label>
                        <div className="tw:relative">
                          <FontAwesomeIcon icon={faClock} className="tw:absolute tw:left-3 tw:top-3 tw:text-gray-400" />
                          <input
                            id="new-duration"
                            type="number"
                            min="1"
                            className={`${twInput} tw:pl-10 ${
                              errors.durationSec ? "tw:border-red-400 tw:ring-1 tw:ring-red-200" : ""
                            }`}
                            value={form.durationSec || ""}
                            onChange={(e) => setForm({ ...form, durationSec: parseInt(e.target.value || 0, 10) })}
                          />
                        </div>
                        {errors.durationSec && (
                          <div className="tw:mt-1 tw:flex tw:items-start tw:gap-1 tw:text-sm tw:text-red-600">
                            <FontAwesomeIcon icon={faExclamationTriangle} className="tw:mt-0.5" />
                            {errors.durationSec}
                          </div>
                        )}
                      </div>
                    </div>

                    <button
                      type="button"
                      className="tw:w-full tw:inline-flex tw:items-center tw:justify-center tw:gap-2 tw:px-4 tw:py-2.5 tw:text-emerald-700 tw:font-medium tw:rounded-sm tw:shadow tw:transition tw:outline-2 tw:outline-emerald-600"
                      onClick={handleCreateFromDisk}
                      disabled={!selectedDisk || !form.videoId}
                    >
                      <FontAwesomeIcon icon={faPlus} />
                      Crea video dal file selezionato
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* === Video List + Detail === */}
          <div className="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-6">
            {/* Lista video */}
            <section className="tw:bg-white tw:rounded-xl tw:shadow-md tw:overflow-hidden tw:border tw:border-gray-100">
              <div className="tw:px-6 tw:py-4 tw:bg-gray-50 tw:flex tw:items-center tw:justify-between">
                <h2 className="tw:text-xl tw:text-gray-800">Elenco Video</h2>
                <span className="tw:bg-blue-500 tw:text-white tw:w-8 tw:h-8 tw:rounded-full tw:flex tw:items-center tw:justify-center tw:text-sm">{videos.length}</span>
              </div>
              <div className="tw:max-h-[500px] tw:overflow-auto">
                {videos.length === 0 ? (
                  <div className="tw:py-12 tw:text-center tw:text-gray-500">Nessun video</div>
                ) : (
                  <table className="tw:w-full tw:text-left">
                    <thead className="tw:bg-gray-50 tw:sticky tw:top-0">
                      <tr>
                        <th className="tw:hidden tw:px-4 tw:py-3 tw:text-xs tw:font-semibold tw:text-gray-600 tw:uppercase">
                          Video ID
                        </th>
                        <th className="tw:px-4 tw:py-3 tw:text-xs tw:font-semibold tw:text-gray-600 tw:uppercase">
                          Titolo
                        </th>
                        <th className="tw:px-4 tw:py-3 tw:text-xs tw:font-semibold tw:text-gray-600 tw:uppercase tw:text-center">
                          Attivo
                        </th>
                        <th className="tw:px-4 tw:py-3 tw:text-xs tw:font-semibold tw:text-gray-600 tw:uppercase tw:text-right">
                          Azioni
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {videos.map((v) => (
                        <tr
                          key={v.video_id}
                          className={`tw:cursor-pointer hover:tw:bg-gray-50 tw:transition tw:duration-150 ${
                            selectedVideoId === v.video_id ? "tw:bg-blue-50 tw:border-l-4 tw:border-l-blue-500" : ""
                          }`}
                          onClick={() => setSelectedVideoId(v.video_id)}
                        >
                          <td className="tw:hidden tw:px-4 tw:py-3 tw:font-mono tw:text-sm tw:text-gray-800">
                            {v.video_id}
                          </td>
                          <td className="tw:px-4 tw:py-3 tw:text-sm tw:text-gray-800">{v.title}</td>
                          <td className="tw:px-4 tw:py-3 tw:text-center">
                            {parseInt(v.active, 10) === 1 ? (
                              <FontAwesomeIcon icon={faCheckCircle} className="tw:text-green-500" />
                            ) : (
                              <FontAwesomeIcon icon={faCircle} className="tw:text-gray-400" />
                            )}
                          </td>
                          <td className="tw:px-4 tw:py-3 tw:text-right">
                            <button
                              type="button"
                              className="tw:p-1.5 tw:text-gray-500 hover:tw:text-red-600 tw:rounded-full hover:tw:bg-red-50"
                              onClick={(e) => {
                                e.stopPropagation();
                                showDeleteConfirm(v);
                              }}
                              aria-label="Elimina video"
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </section>

            {/* Dettagli */}
            <section className="tw:bg-white tw:rounded-xl tw:shadow-md tw:overflow-hidden tw:border tw:border-gray-100">
              <div className="tw:px-6 tw:py-4 tw:bg-gray-50">
                <h2 className="tw:text-xl tw:text-gray-800">
                  {selectedVideo ? "Modifica Video" : "— Seleziona un video —"}
                </h2>
              </div>
              <div className="tw:p-6 tw:space-y-6">
                {selectedVideo ? (
                  <>
                    <div className="tw:grid tw:grid-cols-1 tw:gap-4">
                      <div className="tw:hidden">
                        <label
                          htmlFor="video-id"
                          className="tw:block tw:text-sm tw:font-medium tw:text-gray-700 tw:mb-1"
                        >
                          Video ID
                        </label>
                        <input
                          id="video-id"
                          className={twInput}
                          value={form.videoId}
                          onChange={(e) => setForm({ ...form, videoId: e.target.value })}
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="video-title"
                          className="tw:block tw:text-sm tw:font-medium tw:text-gray-700 tw:mb-1"
                        >
                          Titolo
                        </label>
                        <input
                          id="video-title"
                          className={twInput}
                          value={form.title}
                          onChange={(e) => setForm({ ...form, title: e.target.value })}
                        />
                      </div>
                      <div className="tw:hidden">
                        <label
                          htmlFor="video-url"
                          className="tw:block tw:text-sm tw:font-medium tw:text-gray-700 tw:mb-1"
                        >
                          URL
                        </label>
                        <div className="tw:relative">
                          <FontAwesomeIcon icon={faLink} className="tw:absolute tw:left-3 tw:top-3 tw:text-gray-400" />
                          <input
                            id="video-url"
                            className={`${twInput} tw:pl-10`}
                            value={form.url}
                            onChange={(e) => setForm({ ...form, url: e.target.value })}
                          />
                        </div>
                      </div>
                      <div className="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-4">
                        <div>
                          <label
                            htmlFor="video-duration"
                            className="tw:block tw:text-sm tw:font-medium tw:text-gray-700 tw:mb-1"
                          >
                            Durata (sec)
                          </label>
                          <div className="tw:relative tw:inline-block ">
                            <FontAwesomeIcon
                              icon={faClock}
                              className="tw:text-gray-40 tw:inline-block tw:mr-2 tw:mt-4"
                            />
                            <div className="tw:text-gray-400 tw:inline-block tw:mt-4">
                              {form.durationSec 
                              ?  `${Math.floor(form.durationSec / 60).toString().padStart(2, '0')}:${(form.durationSec % 60).toString().padStart(2, '0')}` 
                              : ""
                              }
                            </div>
                            <input
                              id="video-duration"
                              type="number"
                              min="1"
                              className={`${twInput} tw:pl-10 tw:hidden`}
                              value={form.durationSec || ""}
                              onChange={(e) => setForm({ ...form, durationSec: parseInt(e.target.value || 0, 10) })}
                            />
                            
                          </div>
                        </div>
                        <div>
                          <label
                            htmlFor="video-active"
                            className="tw:block tw:text-sm tw:font-medium tw:text-gray-700 tw:mb-1"
                          >
                            Attivo
                          </label>
                          <select
                            id="video-active"
                            className={`${twInput} tw:pr-8`}
                            value={form.active}
                            onChange={(e) => setForm({ ...form, active: parseInt(e.target.value, 10) })}
                          >
                            <option value={1}>Sì</option>
                            <option value={0}>No</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    <div className="tw:flex tw:gap-3">
                      <button
                        type="button"
                        // className="tw:flex-1 tw:inline-flex tw:items-center tw:justify-center tw:gap-2 tw:px-4 tw:py-2.5 tw:bg-blue-600 hover:tw:bg-blue-700 tw:text-white tw:font-medium tw:rounded-sm tw:shadow tw:transition"
                        className="tw:flex-1 tw:inline-flex tw:items-center tw:justify-center tw:gap-2 tw:px-4 tw:py-2.5 tw:text-blue-700 tw:font-medium tw:rounded-sm tw:shadow tw:transition tw:outline-2 tw:outline-blue-300"
                        onClick={handleSaveVideo}
                        disabled={loading.save}
                      >
                        {loading.save ? (
                          <FontAwesomeIcon icon={faSpinner} className="tw:animate-spin" />
                        ) : (
                          <FontAwesomeIcon icon={faSave} />
                        )}
                        Salva Video
                      </button>
                    </div>

                    <div className="tw:h-px tw:bg-gray-200" />

                    <div>
                      <label className="tw:block tw:text-sm tw:font-medium tw:text-gray-700 tw:mb-2">
                        Target associati
                      </label>
                      <Select
                        isMulti
                        options={targetOptions}
                        value={targetOptions.filter((o) => videoTargets.includes(o.value))}
                        onChange={(vals) => setVideoTargets((vals || []).map((v) => v.value))}
                        placeholder="— Seleziona target —"
                        noOptionsMessage={() => "Nessun target disponibile"}
                        styles={selectStyles}
                        menuPortalTarget={document.body}
                      />
                    </div>

                    <div className="tw:flex tw:gap-3">
                      <button
                        type="button"
                        className="tw:flex-1 tw:inline-flex tw:items-center tw:justify-center tw:gap-2 tw:px-4 tw:py-2.5 tw:text-green-700 tw:font-medium tw:rounded-sm tw:shadow tw:transition tw:outline-2 tw:outline-green-300"
                        onClick={handleSaveTargets}
                        disabled={loading.saveTargets || !form.videoId}
                      >
                        {loading.saveTargets ? (
                          <FontAwesomeIcon icon={faSpinner} className="tw:animate-spin" />
                        ) : (
                          <FontAwesomeIcon icon={faDownload} />
                        )}
                        Salva Target
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="tw:text-center tw:py-12 tw:text-gray-500">
                    <FontAwesomeIcon icon={faFileVideo} className="tw:text-4xl tw:mb-3 tw:text-gray-300" />
                    <p>Seleziona un video per modificarlo.</p>
                  </div>
                )}
              </div>
            </section>
          </div>
        </>
      )}

      {/* === Modal di conferma eliminazione === */}
      {confirmModal.open && (
        <div
          className="tw:fixed tw:inset-0 tw:bg-black/50 tw:z-50 tw:flex tw:items-center tw:justify-center tw:p-4"
          onClick={() => setConfirmModal({ open: false, onConfirm: null, message: "", action: "" })}
        >
          <div
            className="tw:bg-white tw:rounded-xl tw:shadow-xl tw:w-full tw:max-w-sm tw:p-6 tw:space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="tw:text-center">
              <div className="tw:w-12 tw:h-12 tw:mx-auto tw:bg-red-100 tw:rounded-full tw:flex tw:items-center tw:justify-center tw:text-red-600">
                <FontAwesomeIcon icon={faTrash} className="tw:text-xl" />
              </div>
              <h3 className="tw:mt-4 tw:text-lg tw:font-semibold tw:text-gray-800">{confirmModal.message}</h3>
              <p className="tw:mt-2 tw:text-sm tw:text-gray-600">
                {confirmModal.action === "delete-file"
                  ? "Questa azione elimina fisicamente il file dal disco."
                  : "Questa azione elimina il video dal database."}
              </p>
            </div>
            <div className="tw:flex tw:gap-3 tw:pt-2">
              <button
                type="button"
                className="tw:flex-1 tw:px-4 tw:py-2.5 tw:bg-gray-200 hover:tw:bg-gray-300 tw:text-gray-800 tw:font-medium tw:rounded-sm tw:transition"
                onClick={() => setConfirmModal({ open: false, onConfirm: null, message: "", action: "" })}
              >
                Annulla
              </button>
              <button
                type="button"
                className="tw:flex-1 tw:px-4 tw:py-2.5 tw:bg-red-600 hover:tw:bg-red-700 tw:text-white tw:font-medium tw:rounded-sm tw:transition"
                onClick={confirmModal.onConfirm}
                disabled={loading.delete || loading.deleteFile}
              >
                {loading.delete || loading.deleteFile ? <FontAwesomeIcon icon={faSpinner} className="tw:animate-spin" /> : "Elimina"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
